import React, { useState, useRef } from 'react';
import {
  Flex, Text, Box, ActionNote, StateTaxFormInfo,
} from '../../components';
import * as S from '../../styles';
import { STEPS } from '../commonStateTaxData';
import { basicInfoQuestions2022, withholdingQuestions2022 } from './2022/data';
import { basicInfoQuestions2023, withholdingQuestions2023 } from './2023/data';
import FormConsumer from '../../collect-info/FormConsumers';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function ArizonaForm({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  getPdfUrl,
  getInitialQuestions,
  resetInitialQuestions,
  version,
  formLink,
  isOnboardingV2,
}) {
  // Version control
  let basicInfoQuestions;
  let withholdingQuestions;

  switch (version) {
    case '2022':
      basicInfoQuestions = basicInfoQuestions2022;
      withholdingQuestions = withholdingQuestions2022;
      break;
    default:
      basicInfoQuestions = basicInfoQuestions2023;
      withholdingQuestions = withholdingQuestions2023;
      break;
  }

  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  const formatFullName = () => {
    const firstName = `${`${prefilledData.employee_first_name} `}` || '';
    const middleInitial = prefilledData.employee_middle_name;
    const lastName = prefilledData.employee_last_name || '';
    return `${firstName}${middleInitial ? `${middleInitial} ` : ''}${lastName}`;
  };
  prefilledData.employee_name = formatFullName();

  const basicInfoForm = useRef();
  const basicInfoInitQ = getInitialQuestions(basicInfoQuestions);
  const [basicInfo, setBasicInfo] = useState(basicInfoInitQ);

  const exemptionsForm = useRef();

  const withholdingQuestionsInitQ = getInitialQuestions(withholdingQuestions);
  const [amountWithheld, setAmountWithheld] = useState(withholdingQuestionsInitQ);

  const [isExemptFromWithholding, setIsExemptFromWithholding] = useState(data.is_exempt || false);

  const renderBasicInfoForm = (isReviewStep) => (
    <Flex column>
      <FormConsumer
        questions={basicInfo}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = basicInfo.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setBasicInfo(questions);
        }}
        ref={basicInfoForm}
      />
      <Flex column>
        <S.QuestionLabel mb="7px" isRequired>
          I elect an Arizona withholding percentage of zero, and I certify that I expect to have no Arizona tax
          liability for the current taxable year.
        </S.QuestionLabel>
        <S.QuestionWrapper mb="20px">
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={isExemptFromWithholding}
                checked={isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              Yes
            </Flex>
          </label>
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={!isExemptFromWithholding}
                checked={!isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              No
            </Flex>
          </label>
        </S.QuestionWrapper>
      </Flex>
    </Flex>
  );

  const renderWithholdingQuestions = (isReviewStep) => (
    <Flex column>
      <FormConsumer
        questions={amountWithheld}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = amountWithheld.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setAmountWithheld(questions);
        }}
        ref={exemptionsForm}
      />
    </Flex>
  );

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderBasicInfoForm()}
          {!isExemptFromWithholding && renderWithholdingQuestions()}
        </Flex>
        {viewResultAsManager && companyStaff.pii_permission === 'full_access' && (
          <Flex column mt={10} mb={10}>
            <Flex alignItems="center">
              <Text mr={16}>Download signed documents</Text>
              <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                <img src="/j/images/onboarding/download.svg" />
              </a>
            </Flex>
          </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={() => {
            let err = null;
            if (basicInfoForm && basicInfoForm.current) {
              basicInfoForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (exemptionsForm && exemptionsForm.current) {
              exemptionsForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (err) {

            } else {
              // No error, proceed to save
              // - save answers to server
              // - processed to next step
              const basicInfoData = basicInfo.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});

              let amountWithheldData;
              let exemptionQuestionData;

              if (isExemptFromWithholding) {
                amountWithheldData = resetInitialQuestions(withholdingQuestions).reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
              } else {
                amountWithheldData = amountWithheld.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
              }

              const exemptFromWithholdingData = {
                is_exempt: isExemptFromWithholding,
              };
              const exemptionData = {
                ...amountWithheldData,
                ...exemptionQuestionData,
              };

              const data = {
                ...basicInfoData,
                ...exemptionData,
                ...exemptFromWithholdingData,
              };
              saveFormData(data, () => {
                setCurrentStep(STEPS.step2);
                scrollTop();
              });
            }
          }}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderBasicInfoForm(true)}
        {!isExemptFromWithholding && renderWithholdingQuestions(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" />
      </Flex>
    </Flex>
  );

  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>

  );
}

export default ArizonaForm;
